/**
 * Itaú Compromissada COMPONDO o Fluxo de Caixa (call do Veiga 31/08).
 *
 * A âncora do Fluxo era UM número opaco (saldo_total_atual). Agora ela é
 * COMPOSTA: disponível (contas monitoradas) + compromissada Itaú (quando
 * estado == "ok") — e a linha nomeada acima do gráfico é o que torna a
 * composição legível (duas datas: contas de hoje, compromissada de data_base).
 *
 * TRAVA (a única que não se abre mão): ausência de dado NÃO vira movimento.
 * estado != ok ⇒ o valor NÃO entra (não inventamos), mas a LINHA CONTINUA
 * dizendo o motivo e a âncora fica declaradamente PARCIAL — nunca um total
 * menor e calado (a curva cairia R$ 614 mil parecendo saída de caixa).
 *
 * NÃO-DUPLICAÇÃO: a compromissada NÃO é produto Pluggy (era o buraco antigo);
 * a composição soma APENAS disponível + compromissada — investimentos ficam
 * intactos no bloco econômico (teste trava).
 */
import type { SaldoV2Response } from "../../api/types";
import { fmtBRL } from "../../utils/formatters";

export interface ComposicaoAncoraFluxo {
  /** disponível puro (saldo_total_atual do /api/v2/saldo). */
  base: number | null;
  /** compromissada somada? (estado == "ok" e valor presente) */
  compromissadaOk: boolean;
  compromissadaValor: number | null;
  compromissadaDataBase: string | null;
  compromissadaMotivo: string | null;
  compromissadaStale: boolean;
  /** base + compromissada (quando ok); base pura quando parcial. */
  total: number | null;
  /** true = compromissada existe na fonte mas está sem valor hoje — a âncora
   *  está PARCIAL e a tela DIZ (nunca um total menor e calado). */
  parcial: boolean;
  /** 01/09: estado === "sem_posicao" — NÃO é falta de dado: a posição zerou
   *  (resgate provado pelo extrato). A âncora é COMPLETA com parcela ZERO. */
  semPosicao: boolean;
  posicaoAnterior: number | null;
  posicaoAnteriorEm: string | null;
  /** bloco presente na fonte? (ausente = Sync antigo → sem linha, sem parcial) */
  presente: boolean;
}

export function composicaoAncoraFluxo(
  data: SaldoV2Response | null,
): ComposicaoAncoraFluxo {
  const base = data?.saldo_total_atual ?? null;
  const comp = data?.economico?.compromissada ?? null;
  const presente = !!comp && comp.estado !== null;
  const ok =
    presente && comp!.estado === "ok" && comp!.valor_liquido_conforme_pdf !== null;
  const semPosicao = presente && comp!.estado === "sem_posicao";
  const valor = ok ? comp!.valor_liquido_conforme_pdf : null;
  return {
    base,
    compromissadaOk: ok,
    compromissadaValor: valor,
    compromissadaDataBase: comp?.data_base ?? null,
    compromissadaMotivo: comp?.motivo ?? comp?.estado ?? null,
    compromissadaStale: comp?.stale === true,
    // sem_posicao: parcela ZERO — âncora COMPLETA (base + 0), não parcial.
    total: base === null ? null : ok ? base + (valor as number) : base,
    parcial: presente && !ok && !semPosicao,
    semPosicao,
    posicaoAnterior: semPosicao ? (comp?.posicao_anterior ?? null) : null,
    posicaoAnteriorEm: semPosicao ? (comp?.posicao_anterior_em ?? null) : null,
    presente,
  };
}

/** A linha nomeada, acima do gráfico. Sempre presente quando a fonte manda o
 *  bloco — no estado ruim ela é o aviso de que a âncora está parcial. */
export function CompromissadaFluxoLinha({ comp }: { comp: ComposicaoAncoraFluxo }) {
  if (!comp.presente) return null;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2"
      data-testid="fluxo-compromissada-linha"
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-200">
          Itaú Compromissada
        </span>
        <span className="text-[10px] text-white/45">
          compõe o saldo do fluxo · colunas derivadas por aritmética,
          nomenclatura não confirmada contabilmente
        </span>
      </div>
      {comp.compromissadaOk ? (
        <span className="flex items-center gap-2">
          <span
            className="text-sm font-mono tabular-nums font-semibold text-slate-100"
            data-testid="fluxo-compromissada-valor"
          >
            {fmtBRL(comp.compromissadaValor as number, { decimals: 0 })}
          </span>
          <span className="text-[10px] text-white/50">
            data-base {comp.compromissadaDataBase ?? "—"}
          </span>
          {comp.compromissadaStale && (
            <span
              className="text-[10px] text-amber-400/80"
              data-testid="fluxo-compromissada-stale"
            >
              ⚠ data-base antiga — não é de hoje
            </span>
          )}
        </span>
      ) : comp.semPosicao ? (
        <span className="flex items-center gap-2">
          <span
            className="text-sm font-mono tabular-nums font-semibold text-slate-100"
            data-testid="fluxo-compromissada-sem-posicao"
          >
            sem posição aplicada
          </span>
          <span className="text-[10px] text-white/50">
            parcela zero — saldo do fluxo completo
            {comp.posicaoAnterior != null
              ? ` · posição anterior ${fmtBRL(comp.posicaoAnterior, { decimals: 0 })}${comp.posicaoAnteriorEm ? ` em ${comp.posicaoAnteriorEm}` : ""}`
              : ""}
          </span>
        </span>
      ) : (
        <span
          className="text-[11px] text-amber-300/90"
          data-testid="fluxo-compromissada-parcial"
        >
          sem valor hoje ({comp.compromissadaMotivo ?? "indisponível"}) — saldo
          do fluxo PARCIAL, sem a compromissada (ausência de dado não é saída
          de caixa)
        </span>
      )}
    </div>
  );
}
